import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, AlertCircle, FileCheck, Sparkles, Download, Upload, Loader2, Calendar } from "lucide-react";

const MONTH_OPTIONS = [
    { value: "Nov-26", label: "Nov 2026", short: "Nov-26", sub: "Current Month" },
    { value: "Oct-26", label: "Oct 2026", short: "Oct-26", sub: "Last Month" },
    { value: "Sep-26", label: "Sep 2026", short: "Sep-26", sub: "September" },
    { value: "Aug-26", label: "Aug 2026", short: "Aug-26", sub: "August" },
    { value: "Jul-26", label: "Jul 2026", short: "Jul-26", sub: "July" },
    { value: "Jun-26", label: "Jun 2026", short: "Jun-26", sub: "June" },
];

export default function PayMaintenanceModal({ bill, user, onClose, onSuccess }) {
    const defaultPeriod = bill?.period ? (bill.period.includes("Oct") ? "Oct-26" : bill.period.includes("Nov") ? "Nov-26" : bill.period) : "Nov-26";
    const [selectedMonth, setSelectedMonth] = useState(defaultPeriod);
    const [utrNo, setUtrNo] = useState("");
    const [payerName, setPayerName] = useState(user?.name || "Shrutika / Tilak Kalbande");
    const [submitting, setSubmitting] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [extractedInfo, setExtractedInfo] = useState(null);
    const fileInputRef = useRef(null);

    if (!bill) return null;

    const amount = Number(bill.amount || 2500);
    const flatStr = bill.tower && bill.flat_no ? `${bill.tower}-${bill.flat_no}` : (bill.flat_no || "Flat");

    // Scan screenshot with AI OCR against a target month
    const scanScreenshot = async (fileToScan, targetMonth) => {
        if (!fileToScan) return;
        setScanning(true);
        setExtractedInfo(null);
        const formData = new FormData();
        formData.append("file", fileToScan);
        formData.append("target_month", targetMonth);

        try {
            const res = await api.post("/bills/parse-payment-screenshot", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            const data = res.data;
            setExtractedInfo(data);

            if (data.utr_no) {
                setUtrNo(data.utr_no);
            }

            if (data.is_valid) {
                toast.success(`Payment Verified! Fixed ₹2,500, ${targetMonth} Date & 12-Digit UTR matched.`);
            } else {
                toast.warning(`Screenshot scanned. Please check ${targetMonth} validation conditions below.`);
            }
        } catch (err) {
            toast.error("Could not scan screenshot. Please verify or re-upload.");
        } finally {
            setScanning(false);
        }
    };

    // Handle initial file selection
    const handleScreenshotUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadedFile(file);
        const previewUrl = URL.createObjectURL(file);
        setScreenshotPreview(previewUrl);
        await scanScreenshot(file, selectedMonth);
    };

    // Handle Month Selection: if file already uploaded, re-scan against selected month
    const handleMonthChange = (newMonth) => {
        setSelectedMonth(newMonth);
        if (uploadedFile) {
            scanScreenshot(uploadedFile, newMonth);
        }
    };

    const isValid = Boolean(
        extractedInfo &&
        extractedInfo.is_valid === true &&
        extractedInfo.amount_matched === true &&
        extractedInfo.date_matched === true &&
        extractedInfo.utr_matched === true
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid) {
            toast.error(
                extractedInfo?.validation_status ||
                `Please upload a valid payment screenshot of ₹2,500 for ${selectedMonth}.`
            );
            return;
        }

        const cleanUtr = utrNo.trim();
        if (!cleanUtr) {
            toast.error("Please upload payment screenshot or enter the 12-digit UTR");
            return;
        }

        setSubmitting(true);
        try {
            const res = await api.post(`/bills/${bill.bill_id}/pay`, {
                utr_no: cleanUtr,
                paid_by_name: payerName.trim() || user?.name || "Resident",
                payment_mode: "UPI / Online Trf.",
                verification_id: extractedInfo?.verification_id,
                period: selectedMonth
            });
            toast.success(`Payment verified! Official Digital Receipt for ${selectedMonth} ready.`);
            onSuccess(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to generate receipt");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-xs overflow-y-auto animate-fade-in">
            <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-nest-line overflow-hidden my-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-nest-line bg-nest-base/50">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800">
                            <FileCheck size={20} />
                        </div>
                        <div>
                            <h3 className="font-display font-bold text-lg text-nest-ink">
                                Download Digital Receipt
                            </h3>
                            <p className="text-xs text-nest-ink3">
                                Flat {flatStr} · Selected Period: <span className="font-bold text-nest-ink">{selectedMonth}</span> · ₹{amount.toFixed(2)}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-xl text-nest-ink3 hover:text-nest-ink hover:bg-nest-line transition cursor-pointer"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    {/* Notice Banner with Active Month */}
                    <div className="rounded-2xl p-4 bg-gradient-to-r from-emerald-900 to-neutral-900 text-white shadow-xs">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Society Maintenance Dues</span>
                                <p className="font-display text-2xl sm:text-3xl font-black mt-0.5">
                                    ₹{amount.toFixed(2)}
                                </p>
                            </div>
                            <div className="text-right">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 block mb-1">
                                    Receipt Period
                                </span>
                                <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-white/15 text-white border border-white/20">
                                    {selectedMonth}
                                </span>
                            </div>
                        </div>
                        <p className="text-xs text-neutral-300 mt-2">
                            Select the month you paid for below, then upload your payment screenshot. AI OCR will match the date against your selected month and issue the corresponding receipt.
                        </p>
                    </div>

                    {/* SELECT MONTH BUTTON / CONTROLS */}
                    <div className="bg-nest-base/50 p-3.5 rounded-2xl border border-nest-line/80 space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-bold uppercase tracking-wider text-nest-ink flex items-center gap-1.5">
                                <Calendar size={14} className="text-nest-clay" />
                                <span>Select Month to Claim Receipt *</span>
                            </label>
                            <span className="text-[11px] font-mono font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                {selectedMonth}
                            </span>
                        </div>

                        {/* Quick Selection Buttons */}
                        <div className="grid grid-cols-3 gap-2">
                            {MONTH_OPTIONS.slice(0, 3).map((m) => {
                                const isSelected = selectedMonth === m.short;
                                return (
                                    <button
                                        key={m.short}
                                        type="button"
                                        onClick={() => handleMonthChange(m.short)}
                                        className={`py-2 px-2.5 rounded-xl border text-center transition cursor-pointer flex flex-col items-center justify-center gap-0.5 ${
                                            isSelected
                                                ? "bg-nest-ink text-white border-nest-ink shadow-xs ring-2 ring-emerald-500/50"
                                                : "bg-white text-nest-ink border-nest-line hover:border-nest-clay hover:bg-white/80"
                                        }`}
                                    >
                                        <span className="font-mono font-bold text-xs sm:text-sm">{m.short}</span>
                                        <span className={`text-[10px] ${isSelected ? "text-emerald-300 font-semibold" : "text-nest-ink3"}`}>
                                            {m.sub}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Dropdown for older months */}
                        <div className="flex items-center gap-2 pt-0.5">
                            <span className="text-[11px] text-nest-ink3 whitespace-nowrap">More Months:</span>
                            <select
                                value={selectedMonth}
                                onChange={(e) => handleMonthChange(e.target.value)}
                                className="flex-1 py-1.5 px-2.5 rounded-xl border border-nest-line bg-white text-xs font-semibold text-nest-ink outline-none focus:border-nest-clay cursor-pointer"
                            >
                                {MONTH_OPTIONS.map((m) => (
                                    <option key={m.short} value={m.short}>
                                        {m.label} ({m.sub})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Screenshot Upload Dropzone */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-xs font-bold uppercase tracking-wider text-nest-ink">
                                    Upload Payment Successful Screenshot *
                                </label>
                                <span className="text-[11px] text-nest-ink3">
                                    Must show <span className="font-bold text-nest-ink">{selectedMonth}</span> & ₹2,500
                                </span>
                            </div>
                            
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-2xl p-4 sm:p-5 text-center cursor-pointer transition ${
                                    extractedInfo?.is_valid
                                        ? "border-emerald-500 bg-emerald-50/80"
                                        : extractedInfo
                                        ? "border-amber-400 bg-amber-50/60"
                                        : "border-nest-line hover:border-nest-clay bg-nest-base/40 hover:bg-nest-base/70"
                                }`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleScreenshotUpload}
                                    className="hidden"
                                />

                                {scanning ? (
                                    <div className="py-4 flex flex-col items-center justify-center gap-2.5">
                                        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                        <p className="text-xs font-bold text-nest-ink flex items-center gap-1.5">
                                            <Sparkles size={15} className="text-amber-500" />
                                            <span>Scanning screenshot for {selectedMonth}…</span>
                                        </p>
                                        <p className="text-[11px] text-nest-ink3">
                                            Checking: Fixed ₹2,500 · {selectedMonth} Date · 12-Digit UTR
                                        </p>
                                    </div>
                                ) : extractedInfo ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-left">
                                            {screenshotPreview && (
                                                <img
                                                    src={screenshotPreview}
                                                    alt="Payment Screenshot"
                                                    className="w-14 h-20 object-cover rounded-xl border border-neutral-300 shadow-2xs shrink-0"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 text-xs font-bold">
                                                    {extractedInfo.is_valid ? (
                                                        <span className="flex items-center gap-1 text-emerald-800">
                                                            <CheckCircle2 size={16} className="text-emerald-600" />
                                                            <span>{selectedMonth} Payment Verified Successfully!</span>
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-amber-800">
                                                            <AlertCircle size={16} className="text-amber-600" />
                                                            <span>Verification Notice</span>
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-nest-ink font-semibold mt-1">
                                                    {extractedInfo.validation_status}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fileInputRef.current?.click();
                                                }}
                                                className="text-xs font-bold text-emerald-800 underline hover:text-emerald-950 shrink-0 cursor-pointer"
                                            >
                                                Re-upload
                                            </button>
                                        </div>

                                        {/* 3 Conditions Status Badges */}
                                        <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-neutral-200 text-[10px] sm:text-[11px] font-bold">
                                            <div className={`p-1.5 rounded-lg flex items-center justify-center gap-1 ${
                                                extractedInfo.amount_matched ? "bg-emerald-100 text-emerald-900 border border-emerald-300" : "bg-red-50 text-red-700 border border-red-200"
                                            }`}>
                                                <span>{extractedInfo.amount_matched ? "✓" : "✗"} ₹2,500</span>
                                            </div>
                                            <div className={`p-1.5 rounded-lg flex items-center justify-center gap-1 ${
                                                extractedInfo.date_matched ? "bg-emerald-100 text-emerald-900 border border-emerald-300" : "bg-red-50 text-red-700 border border-red-200"
                                            }`}>
                                                <span>{extractedInfo.date_matched ? `✓ ${selectedMonth} Date` : `✗ ${selectedMonth} Mismatch`}</span>
                                            </div>
                                            <div className={`p-1.5 rounded-lg flex items-center justify-center gap-1 ${
                                                extractedInfo.utr_matched ? "bg-emerald-100 text-emerald-900 border border-emerald-300" : "bg-red-50 text-red-700 border border-red-200"
                                            }`}>
                                                <span>{extractedInfo.utr_matched ? "✓ 12-Digit UTR" : "✗ UTR Missing"}</span>
                                            </div>
                                        </div>

                                        {/* Warning Banner when verification fails */}
                                        {!isValid && (
                                            <div className="mt-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-900 text-xs flex items-start gap-2">
                                                <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                                                <div className="text-left">
                                                    <p className="font-bold text-red-950">Receipt Download Locked</p>
                                                    <p className="text-[11px] text-red-700 mt-0.5 leading-relaxed">
                                                        {!extractedInfo.date_matched
                                                            ? `The uploaded screenshot date does not match selected month (${selectedMonth}). If this screenshot is for another month (e.g. ${selectedMonth === "Nov-26" ? "Oct-26" : "Nov-26"}), click that month button above to switch, or upload the ${selectedMonth} screenshot.`
                                                            : !extractedInfo.amount_matched
                                                            ? "The screenshot amount does not match the fixed dues of ₹2,500.00."
                                                            : "A valid 12-digit UPI reference number (UTR) was not found."}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="py-4 flex flex-col items-center justify-center gap-2">
                                        <div className="p-3 rounded-2xl bg-white shadow-2xs text-emerald-700 border border-nest-line">
                                            <Upload size={24} />
                                        </div>
                                        <p className="text-sm font-bold text-nest-ink">
                                            Tap to Upload Payment Screenshot
                                        </p>
                                        <p className="text-xs text-nest-ink3 max-w-xs mx-auto">
                                            Upload your payment success screen for <span className="font-bold text-nest-ink">{selectedMonth}</span>. AI OCR will scan ₹2,500, {selectedMonth} Date & 12-Digit UTR automatically!
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* UTR Input (Autofilled or Manual) */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-bold text-nest-ink">
                                    12-Digit UPI Reference / UTR No. *
                                </label>
                                {extractedInfo?.utr_matched && (
                                    <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                                        Auto-filled via OCR ✓
                                    </span>
                                )}
                            </div>
                            <input
                                required
                                value={utrNo}
                                onChange={(e) => setUtrNo(e.target.value)}
                                placeholder="e.g. 430899112233"
                                className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-2 focus:ring-nest-clay/20 outline-none font-mono font-bold text-sm bg-white text-nest-ink placeholder:text-nest-ink3"
                            />
                        </div>

                        {/* Resident Name on Receipt */}
                        <div>
                            <label className="block text-xs font-bold text-nest-ink mb-1">
                                Name on Receipt (Mr. / Mrs.) *
                            </label>
                            <input
                                required
                                value={payerName}
                                onChange={(e) => setPayerName(e.target.value)}
                                placeholder="e.g. Shrutika / Tilak Kalbande"
                                className="w-full px-3.5 py-2.5 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-2 focus:ring-nest-clay/20 outline-none font-medium text-sm bg-white text-nest-ink placeholder:text-nest-ink3"
                            />
                        </div>

                        {/* Main Download Button */}
                        <button
                            type="submit"
                            disabled={submitting || scanning || !isValid}
                            className={`w-full py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold transition shadow-xs ${
                                isValid
                                    ? "bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer active:scale-95 shadow-md"
                                    : "bg-neutral-200 text-neutral-400 cursor-not-allowed opacity-75"
                            }`}
                        >
                            {submitting ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>Generating Official Receipt for {selectedMonth}…</span>
                                </span>
                            ) : scanning ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>Scanning Screenshot for {selectedMonth}…</span>
                                </span>
                            ) : !extractedInfo ? (
                                <span className="flex items-center gap-2 text-neutral-500 font-semibold">
                                    <Upload size={16} />
                                    <span>Upload {selectedMonth} Screenshot to Download</span>
                                </span>
                            ) : !isValid ? (
                                <span className="flex items-center gap-2 text-red-600 font-semibold">
                                    <AlertCircle size={16} />
                                    <span>
                                        {!extractedInfo.date_matched
                                            ? `Download Locked: Date Mismatched for ${selectedMonth}`
                                            : !extractedInfo.amount_matched
                                            ? "Download Locked: Amount Mismatched"
                                            : "Download Locked: Conditions Incomplete"}
                                    </span>
                                </span>
                            ) : (
                                <>
                                    <Download size={18} />
                                    <span>Download Digital Receipt ({selectedMonth})</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
